import apiClient from './api';
import { v4 as uuidv4 } from 'uuid';

// Event types for Facebook Conversions API
export enum FacebookEventType {
  COMPLETE_REGISTRATION = 'CompleteRegistration',
  INITIATE_CHECKOUT = 'InitiateCheckout',
  SUBSCRIBE = 'Subscribe'
}

// Interface for the user data parameters
interface UserData {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  city?: string;
  country?: string;
  externalId?: string;
  clientUserAgent: string; // Do not hash
}

// Interface for custom data
interface CustomData {
  currency?: string;
  value?: string;
  content_ids?: string[];
  content_type?: string;
  subscriptionId?: string; // Do not hash
}

// Interface for the event parameters
interface EventParams {
  eventName: FacebookEventType;
  eventTime: number;
  eventSourceUrl?: string;
  actionSource: string;
  userData: UserData;
  customData?: CustomData;
}

/**
 * Sends an event to Facebook Conversions API
 * @param params - The event parameters
 * @returns Promise with the response
 */
export const sendFacebookEvent = async (params: EventParams): Promise<any> => {
  try {
    const eventId = uuidv4(); // Generate a unique event ID
    
    const payload = {
      data: [
        {
          event_name: params.eventName,
          event_time: params.eventTime || Math.floor(Date.now() / 1000),
          event_id: eventId,
          event_source_url: params.eventSourceUrl || window.location.href,
          action_source: params.actionSource || 'website',
          user_data: params.userData,
          custom_data: params.customData || {}
        }
      ]
    };

    const response = await apiClient.post('/facebook-events', payload);
    return response.data;
  } catch (error) {
    console.error('Error sending Facebook event:', error);
    throw error;
  }
};

/**
 * Sends a Complete Registration event to Facebook Conversions API
 * @param userData - User data parameters
 * @param url - The URL where the registration occurred
 * @returns Promise with the response
 */
export const sendCompleteRegistrationEvent = async (
  userData: UserData,
  url?: string
): Promise<any> => {
  return sendFacebookEvent({
    eventName: FacebookEventType.COMPLETE_REGISTRATION,
    eventTime: Math.floor(Date.now() / 1000),
    eventSourceUrl: url,
    actionSource: 'website',
    userData
  });
};

/**
 * Sends an Initiate Checkout event to Facebook Conversions API
 * @param userData - User data parameters
 * @param customData - Custom data parameters
 * @param url - The URL where the checkout was initiated
 * @returns Promise with the response
 */
export const sendInitiateCheckoutEvent = async (
  userData: UserData,
  customData?: CustomData,
  url?: string
): Promise<any> => {
  return sendFacebookEvent({
    eventName: FacebookEventType.INITIATE_CHECKOUT,
    eventTime: Math.floor(Date.now() / 1000),
    eventSourceUrl: url,
    actionSource: 'website',
    userData,
    customData
  });
};

/**
 * Sends a Subscribe event to Facebook Conversions API
 * @param userData - User data parameters
 * @param customData - Custom data parameters including the subscription ID
 * @param url - The URL where the subscription occurred
 * @returns Promise with the response
 */
export const sendSubscribeEvent = async (
  userData: UserData,
  customData?: CustomData,
  url?: string
): Promise<any> => {
  return sendFacebookEvent({
    eventName: FacebookEventType.SUBSCRIBE,
    eventTime: Math.floor(Date.now() / 1000),
    eventSourceUrl: url,
    actionSource: 'website',
    userData,
    customData
  });
}; 